import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  Zap,
  EyeOff,
  MessageSquare,
  Sparkles,
  Globe,
  Heart,
  UserPlus,
  Search,
  Smile,
  ChevronDown,
  Star,
} from "lucide-react";
import logoFull from "@/assets/logo-full.png";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import heroPeople from "@/assets/landing-hero-people.jpg";
import phoneUi from "@/assets/landing-phone-ui.jpg";
import friendsImg from "@/assets/landing-friends.jpg";
import globalImg from "@/assets/landing-global.jpg";

const steps = [
  { icon: UserPlus, title: "Create your profile", desc: "Sign up in seconds — or jump in as a guest." },
  { icon: Search, title: "Get matched instantly", desc: "Our smart matchmaker finds someone ready to chat." },
  { icon: MessageSquare, title: "Start talking", desc: "Text, voice or video — whatever feels right." },
  { icon: Smile, title: "Make a connection", desc: "Add friends, keep chats going, build your circle." },
];

const features = [
  { icon: ShieldCheck, title: "Safe & Moderated", desc: "Real-time moderation keeps the community respectful and safe for everyone." },
  { icon: Users, title: "Real People Only", desc: "No bots. No fake profiles. Just verified humans looking for honest conversations." },
  { icon: Zap, title: "Instant Matching", desc: "Tap once and you're talking. No endless swiping or waiting around." },
  { icon: EyeOff, title: "Anonymous or Public", desc: "Stay private with anonymous chats, or open up and meet new friends publicly." },
  { icon: Globe, title: "Global Community", desc: "Connect with people from every corner of the world, 24/7." },
  { icon: Heart, title: "Built for Connection", desc: "Designed to spark real conversations, not algorithmic noise." },
];

const benefits = [
  "Zero bots — every conversation is with a real person",
  "Industry-leading moderation and reporting tools",
  "Free forever to chat — no paywalls on real conversations",
  "Works on any device, no download required",
];

const testimonials = [
  { name: "Maya R.", role: "Student, NYC", quote: "I've met some genuinely amazing people here. It actually feels like the early internet again — real chats, real vibes." },
  { name: "Daniel K.", role: "Designer, Berlin", quote: "Finally a chat app that isn't 80% bots. The matching is fast and people are surprisingly thoughtful." },
  { name: "Sofia A.", role: "Writer, São Paulo", quote: "TalkLot got me out of a lonely patch. Made two friends I now talk to every day." },
];

const faqs = [
  { q: "Is TalkLot free?", a: "Yes. Core chatting is and will always be free. We offer optional Pro perks for power users." },
  { q: "Do I need to share my real identity?", a: "Not at all. You can chat fully anonymously, or share more about yourself when you're ready." },
  { q: "How do you keep it safe?", a: "We combine real-time AI moderation, human review, and easy reporting to keep TalkLot a respectful space." },
  { q: "What devices does it work on?", a: "TalkLot runs in any modern browser — phone, tablet or desktop. No download needed." },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <SEO
        title="TalkLot — Meet New People and Chat Randomly"
        description="Join TalkLot, the random chat app to meet strangers, make friends, and have real conversations through text, voice, and video — free, safe, and instant."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
      </Helmet>
      {/* Top nav */}
      <header className="absolute top-0 left-0 right-0 z-20 px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <img src={logoFull} alt="TalkLot — Random Video Chat" className="h-9 object-contain" decoding="async" />
        <Link
          to="/login"
          className="text-sm font-display font-semibold text-foreground/80 hover:text-foreground transition-colors"
        >
          Log In
        </Link>
      </header>

      {/* HERO */}
      <section
        className="relative min-h-[100dvh] flex flex-col items-center justify-center px-6 pt-28 pb-20 text-center overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, hsl(270 50% 15%) 0%, hsl(0 0% 4%) 60%)",
        }}
      >
        {/* Subtle elegant accents */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(hsl(0 0% 100% / 0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.6) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            }}
          />
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto">
          <motion.img
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            src={logoFull}
            alt="TalkLot — Random Video Chat"
            className="h-28 sm:h-36 mx-auto object-contain mb-8 drop-shadow-2xl"
            decoding="async"
          />

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="font-display font-bold text-4xl sm:text-6xl leading-[1.05] tracking-tight"
          >
            Real Conversations.
            <br />
            <span className="text-gradient-premium">Real People.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed"
          >
            Connect instantly with strangers who actually want to talk. No bots. No fake profiles.
            Just genuine chats.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl gradient-premium text-primary-foreground font-display font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow"
            >
              Join the Chat <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => scrollTo("how-it-works")}
              className="w-full sm:w-auto px-6 py-4 rounded-2xl bg-muted/30 border border-border/30 text-foreground font-display font-semibold text-sm hover:bg-muted/50 transition-colors"
            >
              Learn More
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 flex items-center justify-center gap-2 text-xs text-muted-foreground"
          >
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
              ))}
            </div>
            <span>Loved by 50,000+ chatters worldwide</span>
          </motion.div>
        </div>

        {/* Hero showcase image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="relative z-10 mt-16 w-full max-w-4xl mx-auto px-2"
        >
          <div className="relative rounded-3xl overflow-hidden border border-primary/20 shadow-2xl shadow-primary/30">
            <img
              src={heroPeople}
              alt="Friends laughing while chatting on TalkLot"
              className="w-full h-auto object-cover"
              width={1536}
              height={1024}
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 pointer-events-none mix-blend-overlay" />
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="relative px-6 py-24 max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="How it works"
          title="Get chatting in under a minute"
          subtitle="Four simple steps to your next great conversation."
        />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Phone UI showcase */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-primary/30 to-accent/20 blur-3xl pointer-events-none" />
            <div className="relative rounded-3xl overflow-hidden border border-primary/20">
              <img
                src={phoneUi}
                alt="TalkLot chat interface preview"
                loading="lazy"
                width={1024}
                height={1280}
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>

          <div className="space-y-4 order-1 lg:order-2">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.07 }}
                className="relative rounded-2xl p-5 bg-card/60 border border-border/30 backdrop-blur-sm flex items-start gap-4"
              >
                <div className="w-11 h-11 rounded-xl gradient-premium flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20">
                  <s.icon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-mono text-muted-foreground/60 mb-0.5">
                    STEP {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display font-bold text-base mb-0.5">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        className="relative px-6 py-24"
        style={{
          background:
            "linear-gradient(180deg, hsl(0 0% 4%) 0%, hsl(270 30% 8%) 50%, hsl(0 0% 4%) 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Features"
            title="Built for conversations that matter"
            subtitle="Everything you need to meet real people, safely."
          />
          {/* Global feature banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="mt-12 relative rounded-3xl overflow-hidden border border-primary/20 group"
          >
            <img
              src={globalImg}
              alt="Global community connected through chat"
              loading="lazy"
              width={1280}
              height={1024}
              decoding="async"
              className="w-full h-56 sm:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 sm:px-12">
              <div className="max-w-md">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-primary/80 mb-2">
                  Global community
                </div>
                <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">
                  Talk to anyone, anywhere
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Millions of conversations happening 24/7 across every continent.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-2xl p-6 bg-card/60 border border-border/30 backdrop-blur-sm hover:border-primary/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-display font-bold text-base mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TALKLOT */}
      <section className="relative px-6 py-24 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-accent/30 to-primary/20 blur-3xl pointer-events-none" />
            <div className="relative rounded-3xl overflow-hidden border border-primary/20">
              <img
                src={friendsImg}
                alt="Friends connecting through TalkLot"
                loading="lazy"
                width={1024}
                height={1024}
                decoding="async"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />
            </div>
          </motion.div>

          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-primary/80 mb-3">
              Why TalkLot
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              A chat app that actually <span className="text-gradient-premium">respects you</span>
            </h2>
            <p className="mt-3 text-muted-foreground text-sm sm:text-base">
              We built TalkLot because we missed talking to interesting strangers without the noise.
            </p>
            <div className="mt-8 space-y-3">
              {benefits.map((b, i) => (
                <motion.div
                  key={b}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-muted/20 border border-border/20"
                >
                  <div className="w-6 h-6 rounded-full gradient-premium flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-3 h-3 text-primary-foreground" />
                  </div>
                  <span className="text-sm text-foreground/90 leading-snug">{b}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        className="relative px-6 py-24"
        style={{
          background:
            "linear-gradient(180deg, hsl(0 0% 4%) 0%, hsl(270 25% 7%) 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Testimonials"
            title="Loved by people who hate small talk"
            subtitle="Real words from real members of the community."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl p-6 bg-card/70 border border-border/30 backdrop-blur-sm"
              >
                <div className="flex mb-3">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-3.5 h-3.5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed mb-5">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full gradient-premium flex items-center justify-center text-primary-foreground font-display font-bold text-xs">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-display font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative px-6 py-24 max-w-3xl mx-auto">
        <SectionHeading
          eyebrow="FAQ"
          title="Questions, answered"
          subtitle="The things people ask us most."
        />
        <div className="mt-12 space-y-3">
          {faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div
                key={f.q}
                className="rounded-2xl bg-card/60 border border-border/30 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(open ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-display font-semibold text-sm sm:text-base">{f.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative px-6 py-24">
        <div
          className="max-w-4xl mx-auto rounded-3xl px-8 py-16 sm:py-20 text-center border border-primary/30 relative overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, hsl(270 60% 22%) 0%, hsl(0 0% 6%) 80%)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none opacity-30 bg-gradient-to-b from-primary/10 to-transparent" />
          <div className="relative z-10">
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight">
              Ready to <span className="text-gradient-premium">talk</span>?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
              Join thousands of people having genuine conversations right now.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl gradient-premium text-primary-foreground font-display font-bold text-base shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow"
            >
              Join the Chat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative px-6 py-10 border-t border-border/30">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logoFull} alt="TalkLot — Random Video Chat" className="h-7 object-contain" decoding="async" />
            <span className="text-xs text-muted-foreground">© {new Date().getFullYear()} TalkLot. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/support" className="hover:text-foreground transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

function SectionHeading({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-primary/80 mb-3">
        {eyebrow}
      </div>
      <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{title}</h2>
      {subtitle && <p className="mt-3 text-muted-foreground text-sm sm:text-base">{subtitle}</p>}
    </div>
  );
}

export default LandingPage;